<template>
  <div>
    <aml-approved-modal
      v-model="showModal"
      :operation-alias="approvedOperation ? approvedOperation.alias : ''"
    />
    <operation-created-modal
      v-model="showCreatedModal"
      :operation="createdOperation"
      @input="handleCreatedModalInput"
    />
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useExecutionStore } from '@/store/index'
import { getTimeZone } from '@/utils'
import AmlApprovedModal from '@/components/modal/AmlApprovedModal.vue'
import OperationCreatedModal from '@/components/modal/OperationCreatedModal.vue'

export default {
  name: 'AmlApprovalNotifier',
  components: { AmlApprovedModal, OperationCreatedModal },
  data () {
    return {
      statusByOperationId: {},
      statusInitialized: false,
      knownOperationIds: {},
      createdOperationsQueue: [],
      createdOperation: null,
      showCreatedModal: false,
      approvedOperation: null,
      showModal: false,
      pollInterval: null,
      isFetching: false
    }
  },
  computed: {
    ...mapStores(useExecutionStore),
    isProUser () {
      return String(localStorage.getItem('userRole') || '').toLowerCase() === 'pro'
    }
  },
  mounted () {
    if (!this.isProUser) return
    window.addEventListener('operation-created-by-current-user', this.markOperationAsKnown)
    window.addEventListener('storage', this.handleStorageChange)
    this.fetchAmlStatuses()
    this.pollInterval = setInterval(this.fetchAmlStatuses, 3000)
  },
  beforeDestroy () {
    clearInterval(this.pollInterval)
    window.removeEventListener('operation-created-by-current-user', this.markOperationAsKnown)
    window.removeEventListener('storage', this.handleStorageChange)
  },
  methods: {
    isApproved (value) {
      return value === true || String(value).toLowerCase() === 'true'
    },
    async fetchAmlStatuses () {
      if (this.isFetching) return
      this.isFetching = true
      try {
        const response = await this.executionStore.getOperationIdList()
        const operations = response.data?.operations || []
        this.checkForNewOperations(operations)
        this.checkForNewApprovals(operations)
      } catch (error) {
        console.log('Failed to check AML approvals', error)
      } finally {
        this.isFetching = false
      }
    },
    checkForNewOperations (operations) {
      const nextIds = {}
      const candidateOperations = []
      const wasInitialized = this.statusInitialized

      operations.forEach(operation => {
        const id = String(operation.id)
        nextIds[id] = true
        this.refreshCreatedOperation(operation)
        if (
          wasInitialized &&
          !this.knownOperationIds[id] &&
          !this.wasCreatedByCurrentUser(id) &&
          this.isCreatedToday(operation)
        ) {
          candidateOperations.push(operation)
        }
      })

      this.knownOperationIds = nextIds
      if (!this.isProUser || !candidateOperations.length) return

      candidateOperations
        .sort((a, b) => this.operationTimestamp(a) - this.operationTimestamp(b))
        .forEach(operation => this.createdOperationsQueue.push(operation))
      this.showNextCreatedOperation()
    },
    refreshCreatedOperation (operation) {
      const id = String(operation.id)

      if (String(this.createdOperation?.id) === id) {
        this.createdOperation = operation
      }

      this.createdOperationsQueue = this.createdOperationsQueue.map(queuedOperation =>
        String(queuedOperation.id) === id ? operation : queuedOperation
      )
    },
    selfCreatedOperationIds () {
      try {
        return JSON.parse(localStorage.getItem('selfCreatedOperationIds') || '[]').map(String)
      } catch {
        return []
      }
    },
    wasCreatedByCurrentUser (operationId) {
      return this.selfCreatedOperationIds().includes(String(operationId))
    },
    markOperationAsKnown (event) {
      const operationId = event?.detail?.operationId
      if (!operationId) return
      const id = String(operationId)
      this.$set(this.knownOperationIds, id, true)
      this.createdOperationsQueue = this.createdOperationsQueue.filter(operation => String(operation.id) !== id)
      if (String(this.createdOperation?.id) === id) this.showCreatedModal = false
    },
    handleStorageChange (event) {
      if (event.key !== 'selfCreatedOperationIds') return
      this.selfCreatedOperationIds().forEach(id => this.$set(this.knownOperationIds, id, true))
    },
    operationTimestamp (operation) {
      const value = operation?.timecreate
      if (!value) return 0
      const numericValue = Number(value)
      const normalizedValue = Number.isFinite(numericValue)
        ? (numericValue > 1e12 ? numericValue : numericValue * 1000)
        : (typeof value === 'string' ? value.replace(' ', 'T') : value)
      const timestamp = new Date(normalizedValue).getTime()
      return Number.isNaN(timestamp) ? 0 : timestamp
    },
    isCreatedToday (operation) {
      const timestamp = this.operationTimestamp(operation)
      if (!timestamp) return false

      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: getTimeZone(),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      return formatter.format(new Date(timestamp)) === formatter.format(new Date())
    },
    showNextCreatedOperation () {
      if (this.showCreatedModal || !this.createdOperationsQueue.length) return
      this.createdOperation = this.createdOperationsQueue.shift()
      this.showCreatedModal = true
    },
    handleCreatedModalInput (value) {
      if (value) return
      this.$nextTick(this.showNextCreatedOperation)
    },
    checkForNewApprovals (operations) {
      const nextStatuses = {}

      operations.forEach(operation => {
        const approved = this.isApproved(operation.amlCheck)
        nextStatuses[operation.id] = approved

        if (
          this.statusInitialized &&
          approved &&
          this.statusByOperationId[operation.id] === false
        ) {
          this.approvedOperation = operation
          this.showModal = true
        }
      })

      this.statusByOperationId = nextStatuses
      this.statusInitialized = true
    }
  }
}
</script>
